import React, { useState, useEffect } from 'react';
import { 
  Download,
  Filter,
  TrendingUp,
  BarChart3,
  DollarSign,
  Receipt,
  Users,
  User,
  CreditCard,
  Wallet
} from 'lucide-react';
import Breadcrumb from '../../components/Common/Breadcrumb';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import employeeReportsService from '../../services/employeeReportsService';

const EmployeeReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ employees: [], departments: [] });
  
  // Filter state
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    employeeId: '',
    department: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    loadFilterOptions();
    loadReportData();
  }, []);

  useEffect(() => {
    loadReportData();
  }, [filters]);

  const loadFilterOptions = async () => {
    try {
      const response = await employeeReportsService.getEmployeeFilterOptions();
      if (response.success) {
        setFilterOptions(response.data);
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const loadReportData = async () => {
    try {
      setLoading(true);
      const response = await employeeReportsService.getEmployeeReports(filters);
      if (response.success) {
        setReportData(response.data);
      } else {
        toast.error(response.message || 'Failed to load employee reports');
      }
    } catch (error) {
      console.error('Error loading employee reports:', error);
      if (!error._errorHandled) {
        toast.error('Failed to load employee reports');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    if (key === 'employeeId') {
      const employee = filterOptions.employees.find(emp => emp._id === value);
      setSelectedEmployee(employee || null);
    }
  };

  const resetFilters = () => {
    setFilters({
      startDate: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      employeeId: '',
      department: ''
    });
    setSelectedEmployee(null);
  };

  const exportReport = () => {
    toast.info('Export functionality coming soon');
  };

  const handleEmployeeClick = (employeeId) => {
    setFilters(prev => ({ ...prev, employeeId }));
    const employee = reportData?.employeeExpenseSummary?.find(emp => emp._id.employeeId === employeeId);
    if (employee) {
      setSelectedEmployee({
        _id: employee._id.employeeId,
        employeeName: employee._id.employeeName,
        employeeCode: employee._id.employeeCode,
        department: employee._id.department
      });
    }
  };

  if (loading && !reportData) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading employee reports...</p>
        </div>
      </div>
    );
  }

  const summary = reportData?.summary || {};
  const employeeExpenseSummary = reportData?.employeeExpenseSummary || [];
  const topSpendingEmployees = reportData?.topSpendingEmployees || [];
  const employeeMonthlyTrends = reportData?.employeeMonthlyTrends || [];
  const employeeCategoryBreakdown = reportData?.employeeCategoryBreakdown || [];
  const employeeReimbursementSummary = reportData?.employeeReimbursementSummary || [];
  const employeeAdvanceSummary = reportData?.employeeAdvanceSummary || [];
  const employeeExpenseDetails = reportData?.employeeExpenseDetails || [];

  // Filter monthly trends and category breakdown for selected employee
  const filteredMonthlyTrends = selectedEmployee 
    ? employeeMonthlyTrends.filter(t => t._id.employeeId === selectedEmployee._id)
    : [];
  const filteredCategoryBreakdown = selectedEmployee
    ? employeeCategoryBreakdown.filter(c => c._id.employeeId === selectedEmployee._id)
    : [];

  // Calculate max for chart scaling
  const maxEmployeeAmount = Math.max(...employeeExpenseSummary.map(e => e.totalExpenses || 0), 1);
  const maxMonthlyAmount = Math.max(...filteredMonthlyTrends.map(t => t.totalAmount || 0), 1);
  const maxCategoryAmount = Math.max(...filteredCategoryBreakdown.map(c => c.totalAmount || 0), 1);

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Expenses', path: '/expenses' },
          { label: 'Employee Reports', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Employee Reports</h1>
          <p>Individual employee expense tracking</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={16} />
            Filters
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={exportReport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div style={{
          background: '#fff',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                Employee
              </label>
              <select
                value={filters.employeeId}
                onChange={(e) => handleFilterChange('employeeId', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  background: '#fff'
                }}
              >
                <option value="">All Employees</option>
                {filterOptions.employees.map(emp => (
                  <option key={emp._id} value={emp._id}>
                    {emp.employeeName} ({emp.employeeCode})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#495057' }}>
                Department
              </label>
              <select
                value={filters.department}
                onChange={(e) => handleFilterChange('department', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  background: '#fff'
                }}
              >
                <option value="">All Departments</option>
                {filterOptions.departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
          </div>
          <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
            <button
              onClick={resetFilters}
              style={{
                padding: '8px 16px',
                border: '1px solid #e9ecef',
                borderRadius: '6px',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Inter, sans-serif',
                color: '#495057'
              }}
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">
            {summary.totalEmployees || 0}
          </div>
          <div className="metric-title">Total Employees</div>
          <div className="metric-subtitle">All employees</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">
            {formatCurrency(summary.totalExpenses)}
          </div>
          <div className="metric-title">Total Expenses</div>
          <div className="metric-subtitle">All expense claims</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Receipt size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">
            {summary.totalClaims || 0}
          </div>
          <div className="metric-title">Total Claims</div>
          <div className="metric-subtitle">All claims</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Average</span>
            </div>
          </div>
          <div className="metric-value">
            {formatCurrency(summary.avgExpensePerEmployee)}
          </div>
          <div className="metric-title">Avg per Employee</div>
          <div className="metric-subtitle">Per employee average</div>
        </div>
      </div>

      {/* Top Spending Employees */}
      <div style={{
        background: '#fff',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
          Top Spending Employees
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Employee</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Code</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Department</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Total Expenses</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Claims</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Avg Amount</th>
              </tr>
            </thead>
            <tbody>
              {topSpendingEmployees.length > 0 ? (
                topSpendingEmployees.map((employee, index) => (
                  <tr 
                    key={index} 
                    style={{ 
                      borderBottom: '1px solid #f1f3f4',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleEmployeeClick(employee._id.employeeId)}
                  >
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057', fontWeight: '500' }}>
                      {employee._id.employeeName}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>
                      {employee._id.employeeCode}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>
                      {employee._id.department || '-'}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right', fontWeight: '600' }}>
                      {formatCurrency(employee.totalExpenses)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right' }}>
                      {employee.totalClaims}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right' }}>
                      {formatCurrency(employee.totalExpenses / employee.totalClaims)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                    No employee data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Employee Expense Summary */}
      <div style={{
        background: '#fff',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
          Employee Expense Summary
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Employee</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Department</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Total Expenses</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Approved</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Pending</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Rejected</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Claims</th>
              </tr>
            </thead>
            <tbody>
              {employeeExpenseSummary.length > 0 ? (
                employeeExpenseSummary.map((employee, index) => (
                  <tr 
                    key={index} 
                    style={{ 
                      borderBottom: '1px solid #f1f3f4',
                      cursor: 'pointer',
                      background: selectedEmployee?._id === employee._id.employeeId ? '#f8f9fa' : 'transparent'
                    }}
                    onClick={() => handleEmployeeClick(employee._id.employeeId)}
                  >
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057', fontWeight: '500' }}>
                      {employee._id.employeeName}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>
                      {employee._id.department || '-'}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right', fontWeight: '600' }}>
                      {formatCurrency(employee.totalExpenses)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#43e97b', textAlign: 'right' }}>
                      {formatCurrency(employee.approvedAmount)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#4facfe', textAlign: 'right' }}>
                      {formatCurrency(employee.pendingAmount)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#f5576c', textAlign: 'right' }}>
                      {formatCurrency(employee.rejectedAmount)}
                    </td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right' }}>
                      {employee.totalClaims}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                    No employee expense data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Selected Employee Details */}
      {selectedEmployee && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{
            background: '#fff',
            border: '1px solid #e9ecef',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
              {selectedEmployee.employeeName} - Expense Details
            </h3>
            
            {/* Monthly Trends for Selected Employee */}
            {filteredMonthlyTrends.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '500', color: '#495057' }}>
                  Monthly Expense Trends
                </h4>
                <div style={{ height: '200px', display: 'flex', alignItems: 'flex-end', gap: '8px', paddingBottom: '30px' }}>
                  {filteredMonthlyTrends.map((trend, index) => {
                    const height = (trend.totalAmount / maxMonthlyAmount) * 100;
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    return (
                      <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{
                          width: '100%',
                          height: `${height}%`,
                          background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: '4px 4px 0 0',
                          minHeight: '4px',
                          cursor: 'pointer'
                        }}
                        title={formatCurrency(trend.totalAmount)}
                        ></div>
                        <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--color-text-secondary, #5B6478)', textAlign: 'center' }}>
                          {monthNames[trend._id.month - 1]}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Category Breakdown for Selected Employee */}
            {filteredCategoryBreakdown.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '500', color: '#495057' }}>
                  Category Breakdown
                </h4>
                <div>
                  {filteredCategoryBreakdown.map((category, index) => {
                    const width = (category.totalAmount / maxCategoryAmount) * 100;
                    const colors = [
                      'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                      'linear-gradient(90deg, #f093fb 0%, #f5576c 100%)',
                      'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)',
                      'linear-gradient(90deg, #43e97b 0%, #38f9d7 100%)'
                    ];
                    return (
                      <div key={index} style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-brand-navy, #222448)' }}>
                            {category._id.categoryName || 'Unknown'}
                          </span>
                          <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
                            {formatCurrency(category.totalAmount)}
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '6px',
                          background: '#e9ecef',
                          borderRadius: '3px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${width}%`,
                            height: '100%',
                            background: colors[index % colors.length],
                            borderRadius: '3px'
                          }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Employee Expense Details Table */}
      <div style={{
        background: '#fff',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '24px'
      }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--color-brand-navy, #222448)' }}>
          Recent Expense Claims
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e9ecef' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Claim Number</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Employee</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Department</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Amount</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {employeeExpenseDetails.length > 0 ? (
                employeeExpenseDetails.map((expense, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #f1f3f4' }}>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>{expense.claimNumber}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>{expense.employeeName}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>{expense.department || '-'}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057' }}>{formatDate(expense.claimDate)}</td>
                    <td style={{ padding: '12px', fontSize: '14px', color: '#495057', textAlign: 'right', fontWeight: '600' }}>
                      {formatCurrency(expense.grandTotal)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: expense.status === 'Approved' ? '#d4edda' : 
                                   expense.status === 'Rejected' ? '#f8d7da' : 
                                   expense.status === 'Submitted' ? '#d1ecf1' : '#fff3cd',
                        color: expense.status === 'Approved' ? '#155724' : 
                               expense.status === 'Rejected' ? '#721c24' : 
                               expense.status === 'Submitted' ? '#0c5460' : '#856404'
                      }}>
                        {expense.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary, #5B6478)' }}>
                    No expense claims found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default EmployeeReports;

